import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { CustomerPicker } from "@/pages/estimates/components/Pickers";

const EMPTY = {
  customer: null,
  reg_number: "",
  make: "",
  model: "",
  variant: "",
  year: "",
  fuel_type: "",
  odometer: "",
  color: "",
};

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = (query = "") =>
    api
      .get("/vehicles", { params: query ? { q: query } : {} })
      .then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  // Auto-open the Add-Vehicle dialog when the Quick-Create menu deep-links
  // us here with `?new=1`.  Strip the flag so refresh doesn't retrigger.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("new") === "1") {
      setOpen(true);
      params.delete("new");
      const cleaned = params.toString() ? `?${params.toString()}` : "";
      window.history.replaceState({}, "", window.location.pathname + cleaned);
    }
  }, []);

  const close = () => {
    if (saving) return;
    setOpen(false);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer?.id) {
      toast.error("Pick a customer first — vehicles must belong to one");
      return;
    }
    if (!form.reg_number.trim()) {
      toast.error("Registration number is required");
      return;
    }
    if (!form.make.trim() || !form.model.trim()) {
      toast.error("Make and model are required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        customer_id: form.customer.id,
        reg_number: form.reg_number.trim(),
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year ? Number(form.year) : null,
        fuel_type: form.fuel_type,
        odometer: form.odometer ? Number(form.odometer) : 0,
        color: form.color,
      };
      const r = await api.post("/vehicles", body);
      // The POST body doesn't accept `variant` — PATCH to seed it.
      if (form.variant.trim() && r.data?.id) {
        try {
          await api.patch(`/vehicles/${r.data.id}`, { variant: form.variant.trim() });
        } catch {
          // non-blocking
        }
      }
      toast.success("Vehicle added");
      setForm(EMPTY);
      setOpen(false);
      load(q);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vehicles-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase font-mono tracking-widest text-strawberry mb-1">
            Fleet
          </div>
          <h1 className="text-3xl font-display font-bold">Vehicles</h1>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="vehicles-new-btn"
          className="bg-strawberry text-white hover:bg-strawberry/90 rounded-md h-10 px-4 font-semibold"
        >
          <Plus size={16} weight="bold" className="mr-1.5" />
          New Vehicle
        </Button>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass
          size={16}
          className="absolute left-3 top-3 text-steel/80"
        />
        <Input
          data-testid="vehicles-search-input"
          placeholder="Search reg, make, model, VIN…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            load(e.target.value);
          }}
          className="pl-9 h-10 rounded-none"
        />
      </div>

      <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
        <table className="w-full table-industrial">
          <thead>
            <tr>
              <th>Reg</th>
              <th>Vehicle</th>
              <th>Owner</th>
              <th>Phone</th>
              <th>Added</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="text-center text-steel py-10"
                  data-testid="vehicles-empty"
                >
                  No vehicles yet. Click <span className="font-semibold">New Vehicle</span> to add one.
                </td>
              </tr>
            )}
            {items.map((v) => (
              <tr key={v.id} data-testid={`vehicles-row-${v.id}`}>
                <td className="font-mono text-strawberry font-semibold">
                  {v.reg_number}
                </td>
                <td className="flex items-center gap-2">
                  <Car size={14} className="text-steel/80" />
                  {v.make} {v.model} {v.year ? `· ${v.year}` : ""}
                </td>
                <td>
                  {v.customer ? (
                    <Link
                      to={`/customers/${v.customer.id}`}
                      className="hover:underline"
                    >
                      {v.customer.name}
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="font-mono text-sm">
                  {v.customer?.phone || "—"}
                </td>
                <td className="font-mono text-xs text-steel">
                  {fmtDate(v.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add-Vehicle dialog */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(v) : close())}>
        <DialogContent
          className="max-w-lg"
          data-testid="vehicles-new-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[18px] font-bold text-[#1d3557]">
              New vehicle
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* Customer picker */}
            <div>
              <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                Customer *
              </Label>
              <CustomerPicker
                value={form.customer}
                onChange={(c) => setForm((f) => ({ ...f, customer: c }))}
              />
              <p className="text-[11px] text-[#667085] mt-1">
                Vehicles must belong to a customer. Pick one from the list or
                create a new customer inline.
              </p>
            </div>

            {/* Reg + make/model row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Reg number *
                </Label>
                <Input
                  data-testid="vehicles-new-reg"
                  value={form.reg_number}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, reg_number: e.target.value }))
                  }
                  placeholder="KA05MW1234"
                  className="uppercase font-mono"
                />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Year
                </Label>
                <Input
                  type="number"
                  value={form.year}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, year: e.target.value }))
                  }
                  placeholder="2019"
                  min={1980}
                  max={2100}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Make *
                </Label>
                <Input
                  data-testid="vehicles-new-make"
                  value={form.make}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, make: e.target.value }))
                  }
                  placeholder="Maruti"
                />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Model *
                </Label>
                <Input
                  data-testid="vehicles-new-model"
                  value={form.model}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, model: e.target.value }))
                  }
                  placeholder="Swift"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Variant
                </Label>
                <Input
                  value={form.variant}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, variant: e.target.value }))
                  }
                  placeholder="VXi"
                />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Fuel
                </Label>
                <select
                  value={form.fuel_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, fuel_type: e.target.value }))
                  }
                  className="w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                >
                  <option value="">—</option>
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="cng">CNG</option>
                  <option value="electric">Electric</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Odometer (km)
                </Label>
                <Input
                  type="number"
                  value={form.odometer}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, odometer: e.target.value }))
                  }
                  placeholder="47820"
                  min={0}
                />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557] mb-1 block">
                  Colour
                </Label>
                <Input
                  value={form.color}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, color: e.target.value }))
                  }
                  placeholder="White"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={close}
                disabled={saving}
                data-testid="vehicles-new-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="vehicles-new-save"
                className="bg-strawberry text-white hover:bg-strawberry/90"
              >
                {saving ? "Saving…" : "Save vehicle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
